import { parse_float_or_return_null } from '../helpers';
import { is_sensitive_text_surface, scrub_url_string } from './privacy';

const tracked_attribute_names = [
	'data-hub-event-name',
	'data-hub-event-sub-type',
	'data-hub-value',
	'data-hub-form-element',
	'data-hub-form-id',
	'data-hub-form-name',
	'data-hub-field-name',
	'data-hub-refcodes',
	'data-hub-actblue-refcodes'
];

// Attribute-NAME prefixes captured in addition to the exact names above. Any attribute whose
// name starts with one of these on the click target (or a walked ancestor) is copied verbatim
// into element.attributes — a free-form `object` in the schema, so nothing here needs a
// BigQuery/table-schema change. startsWith prefixes, not regex: cheap, no backtracking, and enough
// for the ad-vendor namespaces seen in the wild. All are generic (no org-specific hard-coding):
//   data-hub-ad-  — our own ad-slot convention a publisher stamps on the slot WRAPPER
//                   (data-hub-ad-slot / -unit / -ssp / -id / -network / -type).
//   data-google-  — Google Publisher Tag (gpt.js) stamps these ITSELF on the slot/iframe, not the
//                   publisher: data-google-query-id (unique per ad request — the useful one) and
//                   data-google-container-id (internal counter). Confirmed against real GAM output;
//                   GPT uses data-google-*, NOT data-gpt-*, so there is deliberately no data-gpt-.
//   data-pw-      — Playwire / Ramp ad manager: data-pw-status (render state) and publisher-authored
//                   data-pw-desktop / data-pw-mobile slot markers. A common third-party mediation
//                   vendor, so this is generic — same category as data-google-, not DK-specific.
// Only lands when the click reaches the parent document — the wrapper, a same-origin "friendly"
// iframe, or the opt-in iframe-focus detector. A real click INSIDE a cross-origin ad iframe never
// reaches us, so nothing from within the creative is captured. Bounded by the caps below.
const tracked_attribute_name_prefixes = [
	'data-hub-ad-',
	'data-google-',
	'data-pw-'
];
const maximum_prefix_matched_attributes = 12;
const maximum_prefix_matched_attribute_value_length = 300;

function escape_css_identifier(identifier) {
	try {
		if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
			return CSS.escape(identifier);
		}
		return String(identifier).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
	} catch {
		return null;
	}
}

function read_attribute(element, attribute_name) {
	return element?.getAttribute?.(attribute_name) || null;
}

function truncate_text(text_value, maximum_length = 300) {
	if (!text_value || typeof text_value !== 'string') {
		return null;
	}
	const normalized_text = text_value.replace(/\s+/g, ' ').trim();
	if (!normalized_text) {
		return null;
	}
	return normalized_text.slice(0, maximum_length);
}

const non_visible_descendant_tag_names = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG']);

/**
 * Text for click analytics should reflect what the user sees, not raw DOM text nodes.
 * textContent on <body> (or a wrapper) includes inline <script> source — innerText does not.
 */
function read_element_text_for_click_analytics(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE || is_sensitive_text_surface(element)) {
		return null;
	}
	try {
		if (typeof element.innerText === 'string') {
			return element.innerText;
		}
	} catch {
		// Some node types throw when reading innerText; fall back below.
	}
	if (typeof document === 'undefined' || typeof document.createTreeWalker !== 'function') {
		return element.textContent;
	}
	const text_parts = [];
	const text_node_walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
		acceptNode(text_node) {
			let ancestor_element = text_node.parentElement;
			while (ancestor_element && ancestor_element !== element) {
				if (non_visible_descendant_tag_names.has(ancestor_element.tagName)) {
					return NodeFilter.FILTER_REJECT;
				}
				ancestor_element = ancestor_element.parentElement;
			}
			return NodeFilter.FILTER_ACCEPT;
		}
	});
	while (text_node_walker.nextNode()) {
		const text_node_value = text_node_walker.currentNode?.nodeValue;
		if (text_node_value) {
			text_parts.push(text_node_value);
		}
	}
	return text_parts.join('');
}

function get_nth_child(element) {
	if (!element?.parentElement) {
		return null;
	}
	return Array.from(element.parentElement.children).indexOf(element) + 1;
}

function get_nth_of_type(element) {
	if (!element?.parentElement) {
		return null;
	}
	const tag_name = element.tagName;
	return Array.from(element.parentElement.children)
		.filter((sibling) => sibling.tagName === tag_name)
		.indexOf(element) + 1;
}

function build_short_selector(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const tag_name = element.tagName.toLowerCase();
	const escaped_id = element.id ? escape_css_identifier(element.id) : null;
	const id = escaped_id ? `#${escaped_id}` : '';
	if (id) {
		return `${tag_name}${id}`;
	}
	const class_selector = element.className && typeof element.className === 'string'
		? element.className.split(/\s+/).filter(Boolean).slice(0, 3).map((class_name) => {
			const escaped_class_name = escape_css_identifier(class_name);
			return escaped_class_name ? `.${escaped_class_name}` : '';
		}).join('')
		: '';
	return `${tag_name}${class_selector}:nth-child(${get_nth_child(element) || 1})`;
}

function build_attribute_payload(element, dom_event = null) {
	const candidate_elements = [];
	const composed_path = typeof dom_event?.composedPath === 'function'
		? dom_event.composedPath()
		: null;
	if (Array.isArray(composed_path)) {
		for (const node of composed_path) {
			if (node && node.nodeType === Node.ELEMENT_NODE) {
				candidate_elements.push(node);
			}
		}
	}
	if (!candidate_elements.includes(element)) {
		candidate_elements.unshift(element);
	}
	if (candidate_elements.length <= 1) {
		let parent_element = element?.parentElement;
		while (parent_element && candidate_elements.length < 8) {
			candidate_elements.push(parent_element);
			parent_element = parent_element.parentElement;
		}
	}

	const attribute_payload = {};
	const scan_elements = candidate_elements.slice(0, 8);
	for (const candidate_element of scan_elements) {
		for (const attribute_name of tracked_attribute_names) {
			if (Object.prototype.hasOwnProperty.call(attribute_payload, attribute_name)) continue;
			const attribute_value = read_attribute(candidate_element, attribute_name);
			if (attribute_value !== null) {
				attribute_payload[attribute_name] = attribute_value;
			}
		}
	}
	collect_prefixed_attributes(scan_elements, attribute_payload);
	return attribute_payload;
}

/**
 * Copies any attribute whose name starts with one of `tracked_attribute_name_prefixes` (ad-slot
 * namespaces) off the walked candidate elements into `attribute_payload`. Reads the live
 * NamedNodeMap directly (there is no exact name to look up) and is defensive about non-element
 * nodes. Exact-name matches already in the payload are never overwritten. Capped by count and
 * per-value length so a page dense with data-* attributes can't bloat the analytics payload.
 */
function collect_prefixed_attributes(scan_elements, attribute_payload) {
	if (tracked_attribute_name_prefixes.length === 0) {
		return;
	}
	// Counts only prefix-matched additions toward the cap. The exact-name list is a fixed finite
	// set and can't cause unbounded growth, so it shouldn't starve open-ended ad-namespace capture.
	let prefix_matched_count = 0;
	for (const candidate_element of scan_elements) {
		const element_attributes = candidate_element?.attributes;
		if (!element_attributes || typeof element_attributes.length !== 'number') {
			continue;
		}
		for (let attribute_index = 0; attribute_index < element_attributes.length; attribute_index += 1) {
			if (prefix_matched_count >= maximum_prefix_matched_attributes) {
				return;
			}
			const attribute = element_attributes[attribute_index];
			const attribute_name = attribute?.name;
			if (
				!attribute_name
				|| Object.prototype.hasOwnProperty.call(attribute_payload, attribute_name)
				|| !tracked_attribute_name_prefixes.some((prefix) => attribute_name.startsWith(prefix))
			) {
				continue;
			}
			const attribute_value = attribute.value;
			if (attribute_value === null || attribute_value === undefined) {
				continue;
			}
			attribute_payload[attribute_name] = typeof attribute_value === 'string'
				? attribute_value.slice(0, maximum_prefix_matched_attribute_value_length)
				: attribute_value;
			prefix_matched_count += 1;
		}
	}
}

function describe_element_for_chain(element) {
	return {
		tag_name: element.tagName.toLowerCase(),
		text: truncate_text(read_element_text_for_click_analytics(element), 120),
		attr_id: element.id || null,
		attr_class: read_attribute(element, 'class'),
		attr_role: read_attribute(element, 'role'),
		attr_href: scrub_url_string(read_attribute(element, 'href')),
		nth_child: get_nth_child(element),
		nth_of_type: get_nth_of_type(element)
	};
}

/**
 * Builds the ancestor chain for a click target. Prefers `dom_event.composedPath()`
 * when available so the walk crosses shadow root boundaries (target → ... → shadow
 * host → host's light-DOM parents). Falls back to `parentElement`, which stops at
 * shadow roots — fine for synthetic test events that don't expose composedPath.
 */
function build_elements_chain(element, dom_event = null) {
	const composed_path = typeof dom_event?.composedPath === 'function'
		? dom_event.composedPath()
		: null;
	if (Array.isArray(composed_path) && composed_path.length > 0) {
		let composed_elements = composed_path.filter(
			(node) => node && node.nodeType === Node.ELEMENT_NODE
		);
		const target_index = composed_elements.indexOf(element);
		if (target_index > 0) {
			composed_elements = composed_elements.slice(target_index);
		} else if (target_index < 0 && element) {
			composed_elements = [element, ...composed_elements];
		}
		return composed_elements.slice(0, 8).map(describe_element_for_chain);
	}
	const chain = [];
	let current_element = element;
	while (current_element && current_element.nodeType === Node.ELEMENT_NODE && chain.length < 8) {
		chain.push(describe_element_for_chain(current_element));
		current_element = current_element.parentElement;
	}
	return chain;
}

/**
 * Returns the innermost element the user actually clicked, peering through any
 * open shadow roots via `composedPath()`. The browser retargets `event.target`
 * to the shadow host on listeners attached outside the shadow tree, so reading
 * `event.target` alone would collapse a click on (say) a label inside our
 * <data-hub-signup-form> down to the host element itself.
 */
export function resolve_composed_event_target_element(dom_event) {
	if (typeof dom_event?.composedPath === 'function') {
		const composed_path = dom_event.composedPath();
		if (Array.isArray(composed_path)) {
			for (const node of composed_path) {
				if (node && node.nodeType === Node.ELEMENT_NODE) {
					return node;
				}
			}
		}
	}
	const fallback_target = dom_event?.target;
	return fallback_target && fallback_target.nodeType === Node.ELEMENT_NODE
		? fallback_target
		: null;
}

/**
 * True when the click target is the page shell (body/html), not a meaningful control.
 * Auto $click capture skips these — they are usually padding misses, not CTAs.
 */
export function should_skip_auto_click_capture_for_target(element) {
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return true;
	}
	if (typeof document !== 'undefined') {
		if (element === document.body || element === document.documentElement) {
			return true;
		}
	}
	const tag_name = element.tagName?.toLowerCase();
	return tag_name === 'body' || tag_name === 'html';
}

export function build_element_context_from_click_event(dom_event, resolved_target_element = null) {
	const target_element = resolved_target_element
		&& resolved_target_element.nodeType === Node.ELEMENT_NODE
		? resolved_target_element
		: resolve_composed_event_target_element(dom_event);
	if (!target_element || target_element.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const bounding_box = target_element.getBoundingClientRect?.() || {};
	const href = read_attribute(target_element, 'href');
	const tracked_attributes = build_attribute_payload(target_element, dom_event);
	let href_url = null;
	try {
		href_url = href ? new URL(href, window.location.href) : null;
	} catch {
		href_url = null;
	}
	return {
		text: truncate_text(read_element_text_for_click_analytics(target_element)),
		tag_name: target_element.tagName.toLowerCase(),
		attr_id: target_element.id || null,
		attr_class: read_attribute(target_element, 'class'),
		attr_role: read_attribute(target_element, 'role'),
		attr_href: scrub_url_string(href),
		attr_name: read_attribute(target_element, 'name'),
		attr_type: read_attribute(target_element, 'type'),
		attr_title: read_attribute(target_element, 'title'),
		attr_aria_label: read_attribute(target_element, 'aria-label'),
		href_domain: href_url?.host || null,
		href_pathname: href_url?.pathname || null,
		is_same_origin_href: href_url ? href_url.origin === window.location.origin : null,
		selector: build_short_selector(target_element),
		position_x: Math.round(dom_event.clientX || 0),
		position_y: Math.round(dom_event.clientY || 0),
		page_x: Math.round(dom_event.pageX || dom_event.clientX || 0),
		page_y: Math.round(dom_event.pageY || dom_event.clientY || 0),
		bbox_left: Math.round(bounding_box.left || 0),
		bbox_top: Math.round(bounding_box.top || 0),
		bbox_right: Math.round(bounding_box.right || 0),
		bbox_bottom: Math.round(bounding_box.bottom || 0),
		bbox_width: Math.round(bounding_box.width || 0),
		bbox_height: Math.round(bounding_box.height || 0),
		scroll_x: Math.round(window.scrollX || 0),
		scroll_y: Math.round(window.scrollY || 0),
		nth_child: get_nth_child(target_element),
		nth_of_type: get_nth_of_type(target_element),
		dom_event: dom_event.type || 'click',
		click_button: dom_event.button ?? null,
		click_count: dom_event.detail ?? null,
		data_hub_value: parse_float_or_return_null(tracked_attributes['data-hub-value']),
		data_hub_form_id: tracked_attributes['data-hub-form-id'] || null,
		data_hub_form_name: tracked_attributes['data-hub-form-name'] || null,
		data_hub_field_name: tracked_attributes['data-hub-field-name'] || null,
		is_data_hub_form_element: tracked_attributes['data-hub-form-element'] === 'true',
		is_outbound_link: href_url ? href_url.origin !== window.location.origin : false,
		is_download_link: Boolean(read_attribute(target_element, 'download')),
		closest_heading_text: truncate_text(target_element.closest('section, article, main, div')?.querySelector?.('h1,h2,h3,h4,h5,h6')?.textContent, 200),
		closest_section_selector: build_short_selector(target_element.closest('section, article, main, div')),
		attributes: tracked_attributes,
		elements_chain: build_elements_chain(target_element, dom_event)
	};
}
